import type { SVGProps } from 'react';

/**
 * MyTXT's own small icon set: one 20-unit grid, one stroke weight, round
 * joins. Drawn for this product so every glyph shares the same optical size.
 */

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function Icon({ size = 18, children, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

export const BoldIcon = (p: IconProps) => (
  <Icon {...p} strokeWidth={1.9}>
    <path className="i-bold" d="M6.5 4.25h4.4a2.9 2.9 0 0 1 0 5.8H6.5zM6.5 10.05h5.1a2.85 2.85 0 0 1 0 5.7H6.5z" />
  </Icon>
);

export const ItalicIcon = (p: IconProps) => (
  <Icon {...p}>
    <path className="i-italic" d="M8.75 4.25h6M5.25 15.75h6M12.1 4.25 7.9 15.75" />
  </Icon>
);

export const StrikeIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M13.9 6.35c-.5-1.35-1.95-2.2-3.85-2.2-2.2 0-3.7 1.1-3.7 2.75 0 .95.45 1.6 1.3 2.1M13.45 12.4c.35.45.5.95.5 1.55 0 1.8-1.65 2.9-3.95 2.9-2.05 0-3.6-.9-4.1-2.45" />
    <path className="i-strike-line" d="M4 10h12" pathLength={1} />
  </Icon>
);

export const CodeIcon = (p: IconProps) => (
  <Icon {...p}>
    <path className="i-code-l" d="m7.25 6.25-3.75 3.75 3.75 3.75" />
    <path className="i-code-r" d="M12.75 6.25l3.75 3.75-3.75 3.75" />
  </Icon>
);

export const QuoteIcon = (p: IconProps) => (
  <Icon {...p}>
    <path className="i-quote-bar" d="M4.5 5v10" />
    <path d="M8.5 6.5h7M8.5 10h7M8.5 13.5h4.5" />
  </Icon>
);

export const BulletListIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M8.5 6h7.5M8.5 10h7.5M8.5 14h7.5" />
    <circle className="i-dot" cx="4.75" cy="6" r=".9" fill="currentColor" stroke="none" />
    <circle className="i-dot" cx="4.75" cy="10" r=".9" fill="currentColor" stroke="none" />
    <circle className="i-dot" cx="4.75" cy="14" r=".9" fill="currentColor" stroke="none" />
  </Icon>
);

export const OrderedListIcon = (p: IconProps) => (
  <Icon {...p}>
    <path className="i-line" d="M9 6h7" pathLength={1} />
    <path className="i-line" d="M9 10h7" pathLength={1} />
    <path className="i-line" d="M9 14h7" pathLength={1} />
    <path d="M3.9 4.6 5 4v4" strokeWidth={1.3} />
    <path d="M3.85 11.6c.15-.55.6-.85 1.15-.85.65 0 1.1.4 1.1.95 0 .9-2.25 1.6-2.25 2.55h2.3" strokeWidth={1.3} />
  </Icon>
);

export const LinkIcon = (p: IconProps) => (
  <Icon {...p}>
    <path className="i-link-a" d="M8.6 11.4a3 3 0 0 0 4.25 0l2.4-2.4a3 3 0 0 0-4.25-4.25l-.7.7" />
    <path className="i-link-b" d="M11.4 8.6a3 3 0 0 0-4.25 0l-2.4 2.4a3 3 0 0 0 4.25 4.25l.7-.7" />
  </Icon>
);

export const PlusIcon = (p: IconProps) => (
  <Icon {...p}>
    <path className="i-plus" d="M10 4.5v11M4.5 10h11" />
  </Icon>
);

export const CloseIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="m6 6 8 8M14 6l-8 8" />
  </Icon>
);

export const ChevronIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="m6 8 4 4 4-4" />
  </Icon>
);

export const CheckIcon = (p: IconProps) => (
  <Icon {...p}>
    <path className="i-check" d="m4.75 10.5 3.25 3.25 7.25-7.5" pathLength={1} />
  </Icon>
);

export const SunIcon = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="10" cy="10" r="3.1" />
    <path d="M10 2.75v1.5M10 15.75v1.5M2.75 10h1.5M15.75 10h1.5M4.87 4.87l1.06 1.06M14.07 14.07l1.06 1.06M4.87 15.13l1.06-1.06M14.07 5.93l1.06-1.06" />
  </Icon>
);

export const MoonIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M15.9 12.2A6.6 6.6 0 0 1 7.8 4.1a6.6 6.6 0 1 0 8.1 8.1Z" />
  </Icon>
);

export const SystemIcon = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="10" cy="10" r="6.5" />
    <path d="M10 3.5a6.5 6.5 0 0 1 0 13Z" fill="currentColor" stroke="none" />
  </Icon>
);

export const StackIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M5 6.5h10M5 10h10M5 13.5h6" />
  </Icon>
);

export const OpenIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3.75 6.25v8a1.5 1.5 0 0 0 1.5 1.5h9.5a1.5 1.5 0 0 0 1.5-1.5v-6a1.5 1.5 0 0 0-1.5-1.5H10L8.25 4.75h-3a1.5 1.5 0 0 0-1.5 1.5Z" />
  </Icon>
);

export const MarkdownIcon = (p: IconProps) => (
  <Icon {...p}>
    <rect x="2.75" y="5" width="14.5" height="10" rx="2" />
    <path d="M5.5 12.25v-4.5l1.9 2.3 1.9-2.3v4.5M13.25 7.75v4.5M11.5 10.6l1.75 1.75L15 10.6" strokeWidth={1.35} />
  </Icon>
);

export const TextIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M5.75 3.75h5.5l3 3v9.5h-8.5Z" />
    <path d="M8 10h4.25M8 13h3" />
  </Icon>
);

export const AlertIcon = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="10" cy="10" r="6.75" />
    <path d="M10 6.5v4" />
    <circle cx="10" cy="13.4" r=".55" fill="currentColor" />
  </Icon>
);

export const GridIcon = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3.75" y="3.75" width="5.25" height="5.25" rx="1.4" />
    <rect x="11" y="3.75" width="5.25" height="5.25" rx="1.4" />
    <rect x="3.75" y="11" width="5.25" height="5.25" rx="1.4" />
    <rect x="11" y="11" width="5.25" height="5.25" rx="1.4" />
  </Icon>
);

export const WindowIcon = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3" y="4" width="14" height="12" rx="2.6" />
    <circle cx="5.9" cy="6.9" r=".55" fill="currentColor" stroke="none" />
    <circle cx="8.1" cy="6.9" r=".55" fill="currentColor" stroke="none" />
  </Icon>
);

export const ExpandIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M11.5 4h4.5v4.5M8.5 16H4v-4.5M16 4l-5 5M4 16l5-5" />
  </Icon>
);

export const ShrinkIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M15.5 8.5H11.5V4.5M4.5 11.5h4v4M11.5 8.5 16 4M8.5 11.5 4 16" />
  </Icon>
);

export const TrayIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M10 3.75v8M6.75 8.75 10 12l3.25-3.25" />
    <path d="M3.75 12.5v1.75a2 2 0 0 0 2 2h8.5a2 2 0 0 0 2-2V12.5" />
  </Icon>
);

export const LineNumbersIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M9 6h7M9 10h7M9 14h5" />
    <path d="M4.5 4.75V7.5M4.5 9.25V12M4.5 13.25V16" strokeWidth={1.3} />
  </Icon>
);

export const CurrentLineIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M5 5.5h10M5 14.5h7" />
    <rect x="3" y="8" width="14" height="4" rx="1.6" fill="currentColor" stroke="none" opacity=".28" />
    <path d="M5 10h9" />
  </Icon>
);

export const SearchIcon = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="9" cy="9" r="4.75" />
    <path d="m12.6 12.6 3.4 3.4" />
  </Icon>
);

export const IndentIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M9 5.5h7M9 10h7M9 14.5h7M3.75 7.5 6.25 10l-2.5 2.5" />
  </Icon>
);

export const OutdentIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M9 5.5h7M9 10h7M9 14.5h7M6.25 7.5 3.75 10l2.5 2.5" />
  </Icon>
);

/** Six teeth: a sixth of a turn brings it back to the same shape, so it can turn once and rest. */
export const GearIcon = (p: IconProps) => (
  <Icon {...p} strokeWidth={1.45}>
    <g className="i-gear">
      <path d="M8.01 4.82 8.49 2.91h3.02l.48 1.91 1.5.87 1.9-.54 1.51 2.61-1.42 1.37v1.74l1.42 1.37-1.51 2.61-1.9-.54-1.5.87-.48 1.91H8.49l-.48-1.91-1.5-.87-1.9.54-1.51-2.61 1.42-1.37V9.13L3.1 7.76l1.51-2.61 1.9.54z" />
      <circle cx="10" cy="10" r="2.35" />
    </g>
  </Icon>
);

/** A small rounded rectangle split into regions: the window layout vocabulary. */
export const LayoutIcon = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3" y="4.25" width="14" height="11.5" rx="2.4" />
    <path d="M10 4.25v11.5M10 10h7" />
  </Icon>
);

export const EyeIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M2.75 10s2.6-5 7.25-5 7.25 5 7.25 5-2.6 5-7.25 5-7.25-5-7.25-5Z" />
    <circle cx="10" cy="10" r="2.25" />
  </Icon>
);

export const SourceIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M5.5 4.75v10.5M5.5 4.75h3M5.5 15.25h3M14.5 4.75v10.5M14.5 4.75h-3M14.5 15.25h-3" />
  </Icon>
);

export const TabKeyIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3.75 10h9.5M10.25 7l3 3-3 3M16.25 6v8" />
  </Icon>
);

export const UndoIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M7.5 6.25 4.25 9.5l3.25 3.25" />
    <path d="M4.5 9.5h7.25a3.75 3.75 0 0 1 0 7.5H9.5" />
  </Icon>
);

export const CloudIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M6.2 15.6a3.7 3.7 0 0 1-.55-7.36 4.6 4.6 0 0 1 8.9 1.04 3.2 3.2 0 0 1-.45 6.32z" />
  </Icon>
);

/** Workspaces: two pages, one resting behind the other. */
export const WorkspacesIcon = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3.5" y="6.5" width="10" height="10" rx="2.6" />
    <path d="M7 3.5h6.9a2.6 2.6 0 0 1 2.6 2.6V13" />
  </Icon>
);

export const MoreIcon = (p: IconProps) => (
  <Icon {...p} strokeWidth={2.2}>
    <path d="M5 10h.01M10 10h.01M15 10h.01" />
  </Icon>
);

export const TrashIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 5.75h12M8 5.5V4.25h4V5.5M5.5 5.75l.7 9.6a1.5 1.5 0 0 0 1.5 1.4h4.6a1.5 1.5 0 0 0 1.5-1.4l.7-9.6" />
  </Icon>
);

export const MailIcon = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3" y="4.75" width="14" height="10.5" rx="2.4" />
    <path d="m3.6 6 6.4 4.7L16.4 6" />
  </Icon>
);

export const ArrowRightIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4.5 10h11M11 5.5l4.5 4.5-4.5 4.5" />
  </Icon>
);

export const ArrowLeftIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M15.5 10h-11M9 5.5 4.5 10 9 14.5" />
  </Icon>
);
